import React, { useContext } from 'react';
import { useTranslation } from 'next-i18next';

import { getErrorMessage } from '@utils/validation';

import {
  FormControl,
  RadioButton,
  RadiosWrapper,
  TextInput,
  BirthdayInput,
  Select,
  InputsWrapper,
  AdditionalInfo,
  SectionHeadingSmall,
} from '@common/components/forms';
import { GENDER_FEMALE, GENDER_MALE } from '@common/constants/genders';
import { getAllCountriesOptions } from '@common/constants/allCountries';
import { getIdentityDocumentsTypes } from '@common/constants/identityDocuments';
import { GridWrapper, GridWrapperItem } from '../../MobileOrderForm.styled';
import { FormContext } from '../../FormWrapper';

const SECTION_TRANSLATION_PREFIX =
  'mobile.orderForm.personalDetails.personalInfoSection';

const PersonalInfoFormSection = () => {
  const { formik, handleChange } = useContext(FormContext);
  const { t } = useTranslation();

  const {
    values,
    handleBlur,
    isSubmitting,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
  } = formik;

  const countriesOptions = getAllCountriesOptions(t);
  const identityDocuments = getIdentityDocumentsTypes(t);
  const currentIdType = identityDocuments.find(
    id => id.value === values.id_type,
  );

  const commonProps = {
    onChange: handleChange,
    onBlur: handleBlur,
    disabled: isSubmitting,
  };

  const isIdNumberInvalid = touched.id_number && !!errors.id_number;
  const isBirthDateInvalid = touched.date_of_birth && !!errors.date_of_birth;

  return (
    <React.Fragment>
      <SectionHeadingSmall spacingTop={['scale', 0]}>
        {t(`${SECTION_TRANSLATION_PREFIX}.title`, 'Persönliche Angaben')}
      </SectionHeadingSmall>
      <InputsWrapper>
        <FormControl
          label={t(`${SECTION_TRANSLATION_PREFIX}.genderLabel`, 'Anrede')}
          labelAs="span"
          hasError={touched.gender && !!errors.gender}
          message={touched.gender && getErrorMessage(t, errors.gender)}
        >
          <RadiosWrapper>
            <RadioButton
              {...commonProps}
              name="gender"
              value={GENDER_FEMALE}
              text={t(`${SECTION_TRANSLATION_PREFIX}.femaleLabel`, 'Frau')}
              checked={values.gender === GENDER_FEMALE}
            />
            <RadioButton
              {...commonProps}
              name="gender"
              value={GENDER_MALE}
              text={t(`${SECTION_TRANSLATION_PREFIX}.maleLabel`, 'Herr')}
              checked={values.gender === GENDER_MALE}
            />
          </RadiosWrapper>
        </FormControl>
      </InputsWrapper>

      <InputsWrapper>
        <FormControl
          label={t(`${SECTION_TRANSLATION_PREFIX}.firstNameLabel`, 'Vorname')}
          hasError={touched.first_name && !!errors.first_name}
          message={touched.first_name && getErrorMessage(t, errors.first_name)}
        >
          <TextInput
            {...commonProps}
            name="first_name"
            value={values.first_name}
            hasError={touched.first_name && !!errors.first_name}
            type="text"
            placeholder={t(
              `${SECTION_TRANSLATION_PREFIX}.firstNamePlaceholder`,
              'Doris',
            )}
          />
        </FormControl>
        <FormControl
          label={t(`${SECTION_TRANSLATION_PREFIX}.lastNameLabel`, 'Nachname')}
          hasError={touched.last_name && !!errors.last_name}
          message={touched.last_name && getErrorMessage(t, errors.last_name)}
        >
          <TextInput
            {...commonProps}
            name="last_name"
            value={values.last_name}
            hasError={touched.last_name && !!errors.last_name}
            type="text"
            placeholder={t(
              `${SECTION_TRANSLATION_PREFIX}.lastNamePlaceholder`,
              'Mustermann',
            )}
          />
        </FormControl>
      </InputsWrapper>

      <InputsWrapper>
        <FormControl
          hasError={isBirthDateInvalid}
          message={
            isBirthDateInvalid && getErrorMessage(t, errors.date_of_birth)
          }
          label={t(
            `${SECTION_TRANSLATION_PREFIX}.birthDateLabel`,
            'Geburtsdatum',
          )}
          noSpacingBottom
        >
          <BirthdayInput
            name="date_of_birth"
            value={values.date_of_birth}
            setFieldValue={(name, value) =>
              handleChange({
                target: { name, value },
              })
            }
            onBlur={handleBlur}
            hasError={isBirthDateInvalid}
            disabled={isSubmitting}
          />
        </FormControl>
        <FormControl
          label={t(
            `${SECTION_TRANSLATION_PREFIX}.nationalityLabel`,
            'Nationalität',
          )}
          hasError={touched.nationality && !!errors.nationality}
          message={
            touched.nationality && getErrorMessage(t, errors.nationality)
          }
        >
          <Select
            name="nationality"
            isSearchable
            value={values.nationality}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            onBlur={handleBlur}
            hasError={touched.nationality && !!errors.nationality}
            placeholder={t(
              `${SECTION_TRANSLATION_PREFIX}.nationalityPlaceholder`,
              'Nationalität',
            )}
            options={countriesOptions}
            disabled={isSubmitting}
          />
        </FormControl>
      </InputsWrapper>

      <InputsWrapper>
        <FormControl
          label={t(`${SECTION_TRANSLATION_PREFIX}.idTypeLabel`, 'Ausweistyp')}
          hasError={touched.id_type && !!errors.id_type}
          message={touched.id_type && getErrorMessage(t, errors.id_type)}
        >
          <Select
            name="id_type"
            isSearchable
            value={values.id_type}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            onBlur={handleBlur}
            hasError={touched.id_type && !!errors.id_type}
            placeholder={t(
              `${SECTION_TRANSLATION_PREFIX}.idTypePlaceholder`,
              'Bitte wählen',
            )}
            options={identityDocuments}
            disabled={isSubmitting}
          />
        </FormControl>
        <div>
          <FormControl
            label={t(
              `${SECTION_TRANSLATION_PREFIX}.idNumberLabel`,
              'Ausweisnummer',
            )}
            hasError={isIdNumberInvalid}
            message={isIdNumberInvalid && getErrorMessage(t, errors.id_number)}
          >
            <TextInput
              {...commonProps}
              name="id_number"
              value={values.id_number}
              hasError={isIdNumberInvalid}
              type="text"
              placeholder={t(
                `${SECTION_TRANSLATION_PREFIX}.idNumberPlaceholder`,
                'Deine Ausweisnummer',
              )}
            />
          </FormControl>
          <AdditionalInfo>
            {t(
              `${SECTION_TRANSLATION_PREFIX}.deliveryIdInfo`,
              'Bei der Lieferung der SIM-Karte musst Du Dich mit derselben ID identifizieren, die Du hier angegeben hast.',
            )}
          </AdditionalInfo>
          {values.id_type && (
            <React.Fragment>
              <AdditionalInfo>
                {t(
                  `${SECTION_TRANSLATION_PREFIX}.avaibleIdNumberFormatsTitle`,
                  'Bitte in einem dieser Formate angeben:',
                )}
              </AdditionalInfo>
              <GridWrapper>
                {currentIdType?.formats.map(format => (
                  <GridWrapperItem key={format}>{format}</GridWrapperItem>
                ))}
              </GridWrapper>
            </React.Fragment>
          )}
        </div>
      </InputsWrapper>
    </React.Fragment>
  );
};

export default PersonalInfoFormSection;
